import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, CheckCircle2, Play } from "lucide-react";

import { sportsService, type Team, type Training } from "../../services/sportsService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { RATINGS, clock, day, labelFrom, squadOrder, trainingConfig, trainingStatus } from "./sportsLogic";

function ScheduleForm({ teams, onClose, onDone }: { teams: Team[]; onClose: () => void; onDone: (row: Training) => void }) {
  const save = useMutation({ mutationFn: sportsService.createTraining, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({ team: Number(text("team")), session_date: text("date"), start_time: text("start"), end_time: text("end"), venue: text("venue"), focus_area: text("focus"), objectives: text("objectives") });
  };
  return (
    <FormModal
      id="training"
      title="Schedule training"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The session could not be scheduled.") : undefined}
      saveLabel="Schedule it"
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The session" divided={false}>
        <Field label="Team" className="sm:col-span-2">
          <select required name="team" className="input mt-1">
            <option value="">Choose a team</option>
            {teams.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name} · {row.sport_name}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input required type="date" name="date" min={todayIso()} defaultValue={todayIso()} className="input mt-1" />
        </Field>
        <Field label="Where" optional>
          <input name="venue" maxLength={200} className="input mt-1" placeholder="e.g. Main pitch" />
        </Field>
        <Field label="Starts">
          <input required type="time" name="start" defaultValue="16:30" className="input mt-1" />
        </Field>
        <Field label="Ends">
          <input required type="time" name="end" defaultValue="18:00" className="input mt-1" />
        </Field>
        <Field label="Focus" optional className="sm:col-span-2" hint="e.g. Passing drills, set pieces, fitness.">
          <input name="focus" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Aims" optional className="sm:col-span-2">
          <textarea name="objectives" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Who came to a session and how they did; only while it is open. */
function Register({ session, canMark }: { session: Training; canMark: boolean }) {
  const queryClient = useQueryClient();
  const squad = useQuery({ queryKey: ["sports", "squad", session.team], queryFn: () => sportsService.getTeamMembers(session.team) });
  const marks = useQuery({ queryKey: ["sports", "session-attendance", session.id], queryFn: () => sportsService.getSessionAttendance(session.id) });
  const mark = useMutation({ mutationFn: sportsService.markAttendance, onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sports", "session-attendance", session.id] }) });
  const byLearner = new Map((marks.data ?? []).map((row) => [row.student, row]));
  const players = squadOrder(squad.data ?? []).filter((row) => row.is_active || byLearner.has(row.student));
  const open = session.status === "scheduled" || session.status === "in_progress";
  const present = (marks.data ?? []).filter((row) => row.attended).length;

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Register</h4>
        <p className="text-xs text-gray-600"><span className="font-semibold text-gray-900">{present}</span> came · {(marks.data ?? []).length - present} away · {players.length - (marks.data ?? []).length} not marked</p>
      </div>
      {squad.isLoading || marks.isLoading ? (
        <div className="mt-1.5 h-16 animate-pulse rounded bg-gray-100" />
      ) : !players.length ? (
        <p className="mt-1.5 text-sm text-gray-500">The team has no players yet.</p>
      ) : (
        <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {players.map((player) => {
            const current = byLearner.get(player.student);
            return (
              <li key={player.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-medium text-gray-900">{player.student_name}</span>
                {canMark && open ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="segmented">
                      {[true, false].map((attended) => (
                        <button
                          key={String(attended)}
                          type="button"
                          aria-pressed={current?.attended === attended}
                          disabled={mark.isPending}
                          onClick={() => mark.mutate({ training_session: session.id, student: player.student, attended, performance_rating: attended ? current?.performance_rating ?? null : null })}
                          className="segmented-option"
                        >
                          {attended ? "Came" : "Away"}
                        </button>
                      ))}
                    </span>
                    {current?.attended && (
                      <select
                        aria-label={`How ${player.student_name} did`}
                        value={current.performance_rating ?? ""}
                        onChange={(event) => mark.mutate({ training_session: session.id, student: player.student, attended: true, performance_rating: event.target.value ? Number(event.target.value) : null })}
                        className="input-compact w-36"
                      >
                        <option value="">How they did…</option>
                        {RATINGS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    )}
                  </span>
                ) : (
                  <span className="text-xs font-medium text-gray-700">
                    {current ? (current.attended ? `Came${current.performance_rating ? ` · ${labelFrom(RATINGS, String(current.performance_rating))}` : ""}` : "Away") : "Not marked"}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {mark.isError && <div className="mt-2">{errorLine(mark.error, "The mark could not be saved.")}</div>}
    </section>
  );
}

/** Training sessions: when, what for, and who came. */
export function TrainingPanel({ sessions, teams, isLoading, isError, onRetry, canManage, onChanged }: {
  sessions?: Training[];
  teams: Team[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [notes, setNotes] = useState("");
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const start = useMutation({ mutationFn: sportsService.startTraining, onSuccess: (row) => done(`${row.team_name} training has started. Mark who came.`) });
  const finish = useMutation({ mutationFn: sportsService.completeTraining, onSuccess: (row) => done(`${row.team_name} training is done: ${row.attendance_count} came.`, true) });
  const cancel = useMutation({ mutationFn: sportsService.cancelTraining, onSuccess: (row) => done(`${row.team_name} training on ${day(row.session_date)} was cancelled.`, true) });

  const columns: RegisterColumn<Training>[] = [
    { header: "Date", cell: (row) => (row.session_date === today ? "Today" : day(row.session_date)), exportValue: (row) => day(row.session_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Time", cell: (row) => `${clock(row.start_time)}–${clock(row.end_time)}`, exportValue: (row) => `${clock(row.start_time)}-${clock(row.end_time)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Team", cell: (row) => <span className="font-medium text-gray-900">{row.team_name}</span>, exportValue: (row) => row.team_name },
    { header: "Focus", cell: (row) => row.focus_area || "—", exportValue: (row) => row.focus_area ?? "", className: "min-w-[10rem] whitespace-normal text-gray-700" },
    { header: "Where", cell: (row) => row.venue || "—", exportValue: (row) => row.venue ?? "", className: "text-gray-700" },
    { header: "Came", cell: (row) => (row.status === "completed" ? row.attendance_count : ""), exportValue: (row) => (row.status === "completed" ? row.attendance_count : ""), align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...trainingStatus(row, today)} />, exportValue: (row) => trainingStatus(row, today).label },
  ];

  const session = viewing !== null ? sessions?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={sessions ? "loaded" : "loading"}
        title="Training"
        description={canManage ? "Open a session to start it, mark who came and close it." : "When the teams train."}
        actions={canManage && (
          <button type="button" disabled={!teams.some((row) => row.is_active)} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Schedule training
          </button>
        )}
        rows={sessions}
        config={trainingConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          [start, finish, cancel].forEach((mutation) => mutation.reset());
          setNotes("");
          setViewing(row.id);
        }}
        openLabel={(row) => (canManage && (row.status === "scheduled" || row.status === "in_progress") && row.session_date <= today ? "Register" : "Open")}
        noun={["session", "sessions"]}
        exportName={{ file: "training", title: "Training Sessions" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No training scheduled." : "No training to show."}
        initialState={(sessions ?? []).some((row) => row.session_date >= today && row.status !== "cancelled") ? undefined : { view: "all" }}
      />

      {session && (
        <DetailsModal
          size="3xl"
          title={`${session.team_name} training`}
          headline={{ label: "When", value: `${session.session_date === today ? "Today" : day(session.session_date)} ${clock(session.start_time)}–${clock(session.end_time)}`, note: [session.venue, session.focus_area].filter(Boolean).join(" · ") || undefined }}
          status={trainingStatus(session, today)}
          facts={[["Run by", session.conducted_by_name], ["Came", session.status === "completed" ? String(session.attendance_count) : undefined]]}
          sections={[
            { title: "Aims", body: session.objectives },
            { title: "Notes", body: session.notes },
            { title: "Why it was cancelled", body: session.cancellation_reason, tone: "danger" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && session.status === "scheduled" && (
                <button type="button" disabled={start.isPending || session.session_date > today} title={session.session_date > today ? "It can start on the day." : undefined} onClick={() => start.mutate(session.id)} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> Start
                </button>
              )}
            </>
          }
        >
          {start.isError && errorLine(start.error, "The session could not start.")}
          <Register session={session} canMark={canManage} />

          {canManage && session.status === "in_progress" && (
            <ActionPanel title="Finish the session" description="The register is locked once it is finished.">
              <Field label="Notes" optional as="div">
                <AIAssistField
                  rows={2}
                  value={notes}
                  onChange={(next) => setNotes(next)}
                  placeholder="What was covered, anything to follow up."
                  context={{ key: "sports.note", params: { kind: "training_notes", id: session.id } }}
                  draftLabel="Help me write it"
                  draftHint="Jot what you did; it writes it up without adding anything."
                />
              </Field>
              <div className="flex justify-end">
                <button type="button" disabled={finish.isPending} onClick={() => finish.mutate({ id: session.id, notes: notes.trim() })} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Finish
                </button>
              </div>
              {finish.isError && errorLine(finish.error, "The session could not be finished.")}
            </ActionPanel>
          )}
          {canManage && (session.status === "scheduled" || session.status === "in_progress") && (
            <ReasonPanel
              title="Cancel the session"
              placeholder="e.g. Heavy rain; the pitch was closed."
              context={{ key: "sports.note", params: { kind: "training_cancel", id: session.id } }}
              button="Cancel the session"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The session could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: session.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {adding && (
        <ScheduleForm
          teams={teams}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            done(`${saved.team_name} training scheduled for ${day(saved.session_date)}.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
